import { Toaster } from "@au-schein-de/au-ui-library";
import { getAllFeatureFlagsHashMap } from "@dransay/feature-flags";
import { getAppSecretsFromEnv, getBaseEnv } from "@dransay/secrets";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { LinksFunction, MetaDescriptor } from "react-router";
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLocation,
	useRouteLoaderData,
} from "react-router";
import { useChangeLanguage } from "remix-i18next/react";
import type { Route } from "./+types/root";
import { ErrorProvider } from "./components/error.context";
import { ErrorDialog } from "./components/error-dialog.component";

export { RootErrorBoundary as ErrorBoundary } from "./components/root-error-boundary.component";

import { useGoogleTagManager } from "./hooks/use-google-tag-manager.hook";
import { useSentryUser } from "./hooks/use-sentry-user.hook";
import { useTelemetry } from "./hooks/use-telemetry.hook";
// Side-effect import so the React Router Vite plugin owns the stylesheet
// tag for `index.css` end-to-end. Importing as `?url` and re-declaring it
// from `links()` made SSR and the client emit different hashed CSS files
// (Tailwind v4 scans different module graphs per build), which double-
// loaded the stylesheet — and its duplicated `@font-face` rules — at
// hydration time. That second parse drove the visible "double blink" and
// font-size shift on cold loads.
import "./index.css";
import { getAppConfig } from "./tooling/app.config";
import { useAdcellTracking } from "./tooling/campaigns/use-adcell-tracking";
import { getGtmNoscriptUrl, getGtmScriptUrl } from "./tooling/gtm-base-url";
import { AVAILABLE_NAMESPACES } from "./tooling/i18n/i18n.constants";
import i18nServer, { localeCookie } from "./tooling/i18n/i18n.server";
import { syncCannabisServiceEnvironmentCookie } from "./tooling/telemetry/service-environment-cookie";
import { getContext } from "./tooling/trpc/get-context";
import { hashPii } from "./utils/hash";

// Satoshi is the page's LCP font and sits at the end of the critical
// request chain (HTML → CSS → font) by default. A stable, preloadable
// copy lives under /fonts so we can declare a matching preload link.
// See `app/index.css` for the matching @font-face declaration.
const SATOSHI_FONT_URL = "/fonts/satoshi-variable.woff2";

export const handle = {
	// In the handle export, we can add a i18n key with namespaces our route
	// will need to load. This key can be a single string or an array of strings.
	// TIP: In most cases, you should set this to your defaultNS from your i18n config
	// or if you did not set one, set it to the i18next default namespace "translation"
	i18n: AVAILABLE_NAMESPACES,
};

export const loader = async ({ request }: { request: Request }) => {
	const locale = await i18nServer.getLocale(request);

	const {
		MARKET,
		SENTRY_DSN,
		WEB_OTLP_URL,
		OTLP_DEV,
		GTM_ID,
		GTM_ENABLED,
		USERCENTRICS_ID,
		SHOW_ROUTE_ERRORS,
		ADCELL_ENABLED,
		CLOUDFLARE_TRANSFORM_IMAGES_URL,
		TURNSTILE_SITE_KEY,
		MAP_TILE_STYLE_URL,
	} = await getAppConfig();

	const { SENTRY_RELEASE, SENTRY_ENVIRONMENT } = getAppSecretsFromEnv();
	const environment = SENTRY_ENVIRONMENT ?? getBaseEnv();

	const context = await getContext({ request });
	const featureFlags = await getAllFeatureFlagsHashMap(
		context.authenticatedUser?.emailAddress,
		context.featureFlagCache,
	);
	const pseudonymousUserId = context.authenticatedUser?.emailAddress
		? await hashPii(context.authenticatedUser.emailAddress)
		: undefined;
	const { enableSentryReplay: isSentryReplayEnabled } = featureFlags;

	return data(
		{
			locale,
			ENV: {
				MARKET,
				SENTRY_DSN,
				WEB_OTLP_URL,
				OTLP_DEV: Boolean(OTLP_DEV && OTLP_DEV.toLowerCase() === "true"),
				ENVIRONMENT: environment,
				GTM_ID,
				GTM_ENABLED,
				USERCENTRICS_ID,
				SHOW_ROUTE_ERRORS,
				ADCELL_ENABLED,
				SENTRY_RELEASE,
				SENTRY_REPLAY_ENABLED: isSentryReplayEnabled,
				CLOUDFLARE_TRANSFORM_IMAGES_URL,
				TURNSTILE_SITE_KEY,
				MAP_TILE_STYLE_URL,
			},
			sentryUserId: pseudonymousUserId,
			featureFlags,
		},
		{
			headers: { "Set-Cookie": await localeCookie.serialize(locale) },
		},
	);
};

export const links: LinksFunction = () => [
	// Preload the Satoshi variable font so it doesn't block LCP by sitting
	// at the end of the CSS → font critical chain. Crossorigin is required
	// for font preload to actually be reused by the subsequent CSS request.
	{
		rel: "preload",
		href: SATOSHI_FONT_URL,
		as: "font",
		type: "font/woff2",
		crossOrigin: "anonymous",
		fetchPriority: "high",
	},
];

export function meta(): MetaDescriptor[] {
	const {
		ENV: { ENVIRONMENT },
	} = useRouteLoaderData("root") ?? {
		ENV: { ENVIRONMENT: "development" },
	};

	return [
		{
			title: "Cannabis Online Shop | DrAnsay",
		},
		{
			name: "description",
			content:
				"Medizinisches Cannabis online kaufen. 1400+ Blüten von 350+ Apotheken ab 2,99 €/g. Ärztliches Rezept ab 14,20 € (Gespräch optional)*.",
		},
		{
			tagName: "link",
			rel: "canonical",
			href: "https://shop.dransay.com",
		},
		{
			tagName: "meta",
			name: "robots",
			content:
				ENVIRONMENT === "production" ? "index, follow" : "noindex, nofollow",
		},
	];
}

export function Layout({ children }: React.PropsWithChildren) {
	const { i18n } = useTranslation();
	const loaderData = useRouteLoaderData<{
		ENV: {
			GTM_ID: string;
			GTM_ENABLED: boolean;
			SHOW_ROUTE_ERRORS: boolean;
			SENTRY_DSN: string;
			ENVIRONMENT: string;
			SENTRY_RELEASE: string;
			SENTRY_REPLAY_ENABLED: boolean;
			MAP_TILE_STYLE_URL?: string;
			CLOUDFLARE_TRANSFORM_IMAGES_URL?: string;
		};
		sentryUserId?: string;
		isV2LogoEnabled: boolean;
	}>("root");
	const ENV = loaderData?.ENV;
	const sentryUserId = loaderData?.sentryUserId;
	const faviconUrl = "/favicon-v3.png";
	const gtmId = ENV?.GTM_ID;
	const gtmMarkup =
		ENV?.GTM_ENABLED && gtmId
			? {
					scriptUrl: getGtmScriptUrl(gtmId),
					noscriptUrl: getGtmNoscriptUrl(gtmId),
				}
			: null;

	return (
		<html lang={i18n.language} dir={i18n.dir()} suppressHydrationWarning={true}>
			<head>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1, interactive-widget=resizes-content"
				/>
				<Meta />
				<link rel="icon" type="image/png" href={faviconUrl} />
				<link rel="apple-touch-icon" href="/apple-touch-icon.png" />
				<Links />
				{ENV?.CLOUDFLARE_TRANSFORM_IMAGES_URL && (
					<link rel="preconnect" href={ENV.CLOUDFLARE_TRANSFORM_IMAGES_URL} />
				)}
				{gtmMarkup && (
					<script
						// biome-ignore lint/security/noDangerouslySetInnerHtml: GTM bootstrap must be inline in head.
						dangerouslySetInnerHTML={{
							__html: `(function(w,d,s,l){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src=${JSON.stringify(gtmMarkup.scriptUrl)};f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer');`,
						}}
					/>
				)}
			</head>
			<body suppressHydrationWarning={true}>
				{gtmMarkup && (
					<noscript>
						<iframe
							src={gtmMarkup.noscriptUrl}
							height="0"
							width="0"
							style={{ display: "none", visibility: "hidden" }}
							title="gtm"
						/>
					</noscript>
				)}
				{children}
				<ScrollRestoration />
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Required to inject environment variables for client-side access
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify({
							SENTRY_DSN: ENV?.SENTRY_DSN,
							ENVIRONMENT: ENV?.ENVIRONMENT,
							SENTRY_RELEASE: ENV?.SENTRY_RELEASE,
							SENTRY_REPLAY_ENABLED: ENV?.SENTRY_REPLAY_ENABLED,
							SENTRY_USER_ID: sentryUserId,
							MAP_TILE_STYLE_URL: ENV?.MAP_TILE_STYLE_URL,
						})}`,
					}}
				/>
				<Scripts />
			</body>
		</html>
	);
}

const PAYMENT_REDIRECT_FROM_PATH = "/checkout/payment/redirect-from";

export default function App({ loaderData }: Route.ComponentProps) {
	const { ENV, locale, sentryUserId } = loaderData;
	const { pathname } = useLocation();
	const isPaymentRedirectFrom = pathname === PAYMENT_REDIRECT_FROM_PATH;

	useTelemetry(ENV.WEB_OTLP_URL, ENV.OTLP_DEV, ENV.SENTRY_RELEASE);

	useSentryUser({ userId: sentryUserId });

	useGoogleTagManager({
		id: ENV.GTM_ID,
		enabled: ENV.GTM_ENABLED,
	});

	useEffect(() => {
		syncCannabisServiceEnvironmentCookie(ENV.GTM_ENABLED);
	}, [ENV.GTM_ENABLED]);

	useAdcellTracking({
		enabled: (ENV.ADCELL_ENABLED ?? false) && !isPaymentRedirectFrom,
	});

	// when we support a currency selection and language selection we can remove this
	useChangeLanguage(locale);

	return (
		<NuqsAdapter>
			<ErrorProvider>
				<Outlet />
				<ErrorDialog />
				<Toaster />
			</ErrorProvider>
		</NuqsAdapter>
	);
}
